import { useState } from "react";

import { useLocalization } from "@fluent/react";
import ApiHttp from "byzantine/src/ApiHttp";
import { useNotificationContext } from "cerulean";

import {
  extractFilenameFromResponse,
  triggerFileDownload,
} from "../../../../components/DownloadTransactionsDialog/transactionDownloadHelpers";

import {
  extractBackendCode,
  pickExportErrorMapping,
} from "./exportErrorMapping";

import type Account from "byzantine/src/Account";
import type { ExportFormat } from "../constants";
import type { FileFormat } from "../../../../components/DownloadTransactionsDialog/transactionDownloadHelpers";

interface UseTransactionExportOptions {
  accounts: Account[];
  selectedFormat: ExportFormat;
  selectedTypeCodes: string[];
  minDate: string;
  maxDate: string;
}

interface UseTransactionExportResult {
  isExporting: boolean;
  handleExport: (fileName?: string) => Promise<void>;
}

const useTransactionExport = ({
  accounts,
  selectedFormat,
  selectedTypeCodes,
  minDate,
  maxDate,
}: UseTransactionExportOptions): UseTransactionExportResult => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (fileName?: string) => {
    if (accounts.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const payload: {
        account_uuids: string[];
        min_date: string;
        max_date: string;
        format: ExportFormat;
        bai_codes?: string[];
      } = {
        account_uuids: accounts.map((a) => a.id),
        min_date: minDate,
        max_date: maxDate,
        format: selectedFormat,
      };
      if (selectedTypeCodes.length > 0) {
        payload.bai_codes = selectedTypeCodes;
      }

      // rawResponse so the structured error envelope (with its `id`) survives
      // on failure. Attachment downloads already return the full response on
      // success regardless (see isRawResponse in byzantine ApiHttp/types).
      const response = await ApiHttp.fetch(
        "reports/transactions_download",
        { method: "POST", rawResponse: true },
        payload
      );

      const downloadName =
        fileName ||
        extractFilenameFromResponse(
          response.headers["content-disposition"],
          selectedFormat as FileFormat
        );
      triggerFileDownload(response.data, downloadName);
      sendNotification({
        type: "success",
        text: l10n.getString("transaction-export-success"),
      });
    } catch (error) {
      // Localize on the client from the API's stable error code; unknown codes
      // fall back to the generic export-failed string.
      const { ftlId, vars } = pickExportErrorMapping(extractBackendCode(error));
      sendNotification({
        type: "negative",
        text: l10n.getString(ftlId, vars),
      });
    } finally {
      setIsExporting(false);
    }
  };

  return { isExporting, handleExport };
};

export default useTransactionExport;
